// UTF-8规则
//
// 1个字节（07）：0xxxxxxx
// 2个字节（11）：110xxxxx 10xxxxxx
// 3个字节（16）：1110xxxx 10xxxxxx 10xxxxxx
// 4个字节（21）：11110xxx 10xxxxxx 10xxxxxx 10xxxxxx
// 除去定值部分，其余二进制位为 Unicode 编码

import Unicode from './Unicode.js'

// UTF-8 首字节补码列表，索引代表后续字节数
const FIRST_COMPLEMENTS = [
  0,
  0b11000000,
  0b11100000,
  0b11110000
]

// UTF-8 后续字节补码
const OTHER_COMPLEMENT = 0b10000000

// 编码：Unicode 上限（不包括），索引代表后续字节数
const ENCODE_LIMITS = [
  1 << 7,
  1 << 11,
  1 << 16,
  1 << 21
]

// 解码：首字节 Unicode 上限（不包括），索引代表后续字节数
const DECODE_LIMITS = [
  0b10000000,
  0b11100000,
  0b11110000,
  0b11111000
]

// 判断是否后续字节
const isOtherPart = code => code < 0b11000000 && code >= OTHER_COMPLEMENT

// 拆分字节
const split = (code, times) => {
  const codes = []
  // 拆分原编码尾段，补码，并填充为头部新字节
  for (let p = 0; p < times; p++) {
    codes.unshift(code & 0b111111 | OTHER_COMPLEMENT)
    code >>= 6
  }
  // 填充首个字节
  codes.unshift(code | FIRST_COMPLEMENTS[times])
  return codes
}

// UTF8
const UTF8 = { Unicode }

// 编码转换：Unicode => UTF-8
UTF8.fromUnicodes = codes => {
  const newCodes = []
  for (const code of codes) {
    // 判断字符编码是否需要拆分，返回拆分次数，或 -1
    const splitTimes = ENCODE_LIMITS.findIndex(value => code < value)
    splitTimes > 0 ? newCodes.push(...split(code, splitTimes)) : newCodes.push(code)
  }
  return newCodes
}

// 编码转换：UTF-8 => Unicode
UTF8.toUnicodes = codes => {
  const newCodes = []
  const len = codes.length

  for (let i = 0; i < len; i++) {
    const code = codes[i]

    // 判断当前字节是否拆分首字节，返回后续字节数，或 -1
    let mergeTimes = DECODE_LIMITS.findIndex((value, index) => code >= FIRST_COMPLEMENTS[index] && code < value)

    // 有后续字节且能被提供，则进入拼接程序
    if (mergeTimes > 0 && i + mergeTimes < len) {
      // 有效主编码（先提取首个字节有效编码）
      let newCode = code - FIRST_COMPLEMENTS[mergeTimes]

      let skip = 0

      do {
        // 提取后续字符编码
        const nextCode = codes[i + ++skip]

        // 检测该字符编码是否符合后续字节，未通过则跳出循环验证计算
        if (!isOtherPart(nextCode)) {
          break
        }

        // 将后续有效编码连接到主编码尾部
        newCode = newCode << 6 | nextCode - OTHER_COMPLEMENT
      } while (--mergeTimes > 0)

      // 拼接完成，记录完整的有效主编码，并跳过后续字节字符处理
      if (mergeTimes === 0) {
        newCodes.push(newCode)
        i += skip
        continue
      }
    }

    newCodes.push(code)
  }

  return newCodes
}

// 对字符串进行 UTF-8 编码
UTF8.encode = str => {
  const unicodes = Unicode.fromString(str)
  const utf8Codes = UTF8.fromUnicodes(unicodes)
  return Unicode.toString(utf8Codes)
}

// 对字符串进行 UTF-8 解码
UTF8.decode = str => {
  const utf8Codes = Unicode.fromString(str)
  const unicodes = UTF8.toUnicodes(utf8Codes)
  return Unicode.toString(unicodes)
}

export default UTF8
